import { useEffect, useState } from 'react';
import { MEDIA_TYPE } from '@constants/file';

// A stalled request fires neither loadedmetadata nor error; without a deadline the save gate
// would stay shut indefinitely.
const MEASURE_TIMEOUT_MS = 10_000;

export const loadMetadata = (link, timeoutMs = MEASURE_TIMEOUT_MS) =>
    new Promise(resolve => {
        const video = document.createElement('video');
        const settle = metadata => {
            clearTimeout(timer);
            video.removeAttribute('src');
            resolve(metadata);
        };
        const timer = setTimeout(() => settle(null), timeoutMs);
        video.preload = 'metadata';
        video.onloadedmetadata = () =>
            settle(
                Number.isFinite(video.duration)
                    ? {
                          duration: video.duration,
                          width: video.videoWidth || undefined,
                          height: video.videoHeight || undefined,
                      }
                    : null,
            );
        video.onerror = () => settle(null);
        video.src = link;
    });

export const collectUnmeasuredLinks = mediaGroups =>
    [
        ...new Set(
            mediaGroups
                .filter(Boolean)
                .flat()
                .filter(m => m?.type === MEDIA_TYPE.VIDEO && m.link && m.duration == null)
                .map(m => m.link),
        ),
    ].sort();

// Re-measures videos from a saved campaign, which arrive as {link, type}. Duration and dimensions
// both come off the metadata event; only byte size is unrecoverable, so saved media skips size
// rules alone. Serving the stamped asset row with campaign media would retire this hook.
// isMeasuring holds the save gate shut until every link settles; a failed load counts as settled.
export const useSavedVideoDurations = (...mediaGroups) => {
    const links = collectUnmeasuredLinks(mediaGroups);
    const linksKey = links.join('|');
    const [durations, setDurations] = useState({});
    const [settledLinks, setSettledLinks] = useState({});

    useEffect(() => {
        if (!linksKey) return undefined;
        let cancelled = false;
        Promise.all(linksKey.split('|').map(link => loadMetadata(link).then(metadata => [link, metadata]))).then(
            entries => {
                if (cancelled) return;
                const measured = Object.fromEntries(entries.filter(([, metadata]) => metadata != null));
                if (Object.keys(measured).length) {
                    setDurations(prev => ({ ...prev, ...measured }));
                }
                setSettledLinks(prev => ({ ...prev, ...Object.fromEntries(entries.map(([link]) => [link, true])) }));
            },
        );
        return () => {
            cancelled = true;
        };
    }, [linksKey]);

    return { durations, isMeasuring: links.some(link => !settledLinks[link]) };
};

export const withMeasuredDurations = (media, durations) => {
    if (!media?.length) return media;
    return media.map(item =>
        item?.type === MEDIA_TYPE.VIDEO && item.duration == null && durations[item.link] != null
            ? { ...item, ...durations[item.link] }
            : item,
    );
};

export const overridesWithMeasuredDurations = (overrides, durations) => {
    if (!overrides?.length) return overrides;
    return overrides.map(o => (o?.media == null ? o : { ...o, media: withMeasuredDurations(o.media, durations) }));
};
